import React, { useState } from 'react'
import theme from '@app/ui/theme'
import { ButtonProps, View } from 'react-native'
import * as S from './styled'
import CourseScorecard from '../CourseScorecard/CourseScorecard'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import { RatingTemplate } from '../../../types/Templates'
import { useAxios } from '@common/hooks/useAxios'
import { useSnackbar } from '@app/snackbar'
import useUser from '@common/hooks/useUser'
import Modal, { ModalHeader, ModalFooter } from '@app/components/Modal'
import {
  CourseOptionSelect,
  CourseOptionSelectItem,
} from '@app/evaluation-settings-menu/HeaderArea.styles'

type Props = {
  isVisible: boolean
  disabled?: boolean
  courseId?: number
  branchId?: string
  projectCourse?: any
  holeIds?: string[]
  closeFn?: any
  isComposite?: boolean
  template?: RatingTemplate
}

const CourseScorecardModal: React.FC<Props> = ({
  isVisible = false,
  disabled = false,
  courseId,
  branchId,
  projectCourse = null,
  holeIds,
  closeFn,
  isComposite = false,
  template,
}) => {
  const [openSnackbar] = useSnackbar()
  const user = useUser()
  const [isDowlnoading, setIsDownloading] = useState(false)
  const [units, setUnits] = useState<'metric' | 'imperial'>('metric')
  const [developerMode, setDeveloperMode] = useState(false)

  const downloadScorecard = async () => {
    try {
      setIsDownloading(true)
      const parsedHoleIds =
        holeIds ??
        (projectCourse?.holes.map((hole) => hole.physicalID) as
          | string[]
          | undefined)

      if (!parsedHoleIds || !branchId) {
        return
      }

      const body = JSON.stringify({
        title: (projectCourse?.name as string | undefined) ?? '',
        branchId: +branchId,
        holeIds: parsedHoleIds,
      })

      const resp = await fetch(
        'https://forge.mappingindustries.com/pdf/liverate-scorecard',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        }
      )

      if (!resp.ok) {
        openSnackbar('Failed to download scorecard')
        return
      }

      resp.arrayBuffer().then((buffer) => {
        const url = window.URL.createObjectURL(new Blob([buffer]))
        const link = document.createElement('a')
        link.setAttribute('href', url)
        link.setAttribute('download', 'scorecard.pdf')
        link.click()
        setIsDownloading(false)
      })
    } catch (error) {
      openSnackbar('Failed to download scorecard')
      setIsDownloading(false)
    }
  }

  return (
    <Modal isOpen={isVisible} onClose={closeFn} width="90vw" height="90vh">
      <ModalHeader>
        <S.HeaderRow>
          <S.Title>{projectCourse?.name ?? 'Scorecard'}</S.Title>
          {Number(user.accountType) >= 10 && (
            <S.UnitToggle>
              <CourseOptionSelect>
                <CourseOptionSelectItem
                  selected={developerMode}
                  onPress={() => setDeveloperMode(!developerMode)}
                >
                  <span>{'<>'}</span>
                </CourseOptionSelectItem>
              </CourseOptionSelect>
            </S.UnitToggle>
          )}
          <S.UnitToggle>
            <CourseOptionSelect>
              <CourseOptionSelectItem
                selected={units === 'imperial'}
                onPress={() => setUnits('imperial')}
              >
                <span>Imperial</span>
              </CourseOptionSelectItem>
              <CourseOptionSelectItem
                selected={units === 'metric'}
                onPress={() => setUnits('metric')}
              >
                <span>Metric</span>
              </CourseOptionSelectItem>
            </CourseOptionSelect>
          </S.UnitToggle>
        </S.HeaderRow>
      </ModalHeader>
      <S.ContentWrapper>
        <CourseScorecard
          holeIds={holeIds}
          courseId={courseId}
          branchId={branchId}
          projectCourse={projectCourse}
          isComposite={isComposite}
          template={template}
          units={units}
          developerMode={developerMode}
        />
      </S.ContentWrapper>
      <ModalFooter>
        <RoundedButton
          onPress={downloadScorecard}
          disabled={isDowlnoading}
          title={isDowlnoading ? 'Downloading...' : 'Download Scorecard'}
          testId="btn-print"
          style={{
            backgroundColor: theme.colors.secondary2,
            height: '48px',
          }}
        />
      </ModalFooter>
    </Modal>
  )
}

export default CourseScorecardModal
